// Approvals — EXPENSES ONLY (owner 2026-09-19, plan/rbac-approvals-expenses.md). Documents are NOT
// approved here: a document is confirmed in place from the dashboard drafts panel and the
// Sales/Purchases screens (the Confirm action = approve+issue). This screen is the approver's
// action queue for EXPENSE CLAIMS — approve / reject (with a reason) / pay a submitted expense.
//
// Approvers = admins + governors (rbac_service.is_approver). The queue is capability-gated against
// the server's own predicate (GET /expense-access); a non-approver sees their OWN submitted
// expenses (read-only, with any reject reason) instead of the whole-company queue. The server still
// enforces every decision — the UI just doesn't offer what the API would 403.
import { html, signal, navigate, effect } from '../shared/t4.js';
import { api } from '../shared/api.js';
import { tenantShell, refreshHeaderCounts, notificationsTabs } from './tshell.js';
import { fmtMoney, formatDate, baseCurrencySymbol } from './bank_helpers.js';
import { t } from '../shared/i18n.js';
import { DOC_TYPES } from './documents.js';

// type_code → /co/{id}/{segment} route segment, derived from DOC_TYPES. Kept exported because
// contacts.js uses it for the statement Ref click-through (this screen no longer approves documents,
// but the mapping still belongs to the same module for its other consumers).
export const SEG = Object.keys(DOC_TYPES).reduce((m, key) => { m[DOC_TYPES[key].type] = key; return m; }, {});

const PENDING = ['pending', 'review'];
const RESOLVED = ['approved', 'reimbursed', 'rejected'];
const STATE_PILL = { approved: 'pill-paid', reimbursed: 'pill-paid', rejected: 'pill-overdue',
  pending: 'pill-due', review: 'pill-due' };
const STATE_LABEL = { pending: 'Awaiting review', review: 'In review', approved: 'Approved',
  reimbursed: 'Paid', rejected: 'Rejected' };
const stLabel = s => t('approvals.st_' + s, STATE_LABEL[s] || s);
const stClass = s => 'pill ' + (STATE_PILL[s] || 'pill-due');

export function approvalsScreen(companyId){
  const caps = signal(null), ref = signal(null);
  const rows = signal([]), loading = signal(true), err = signal('');
  const pQ = signal(''), rQ = signal('');
  const rowErr = signal({});
  let loadSeq = 0;

  const isApprover = () => !!(caps.value && caps.value.is_approver);
  const gateOn = () => !!(caps.value && caps.value.approval_required);
  const sym = () => (ref.value && ref.value.currency_symbol) || baseCurrencySymbol();
  const dfmt = () => (ref.value && ref.value.date_format) || 'yyyy-MM-dd';

  (async () => { try { ref.value = await api.get('/companies/' + companyId + '/documents-reference'); } catch {} })();
  (async () => { try { caps.value = await api.get('/companies/' + companyId + '/expense-access'); } catch {} })();

  async function load(){
    const seq = ++loadSeq;
    loading.value = true;
    const mine = !isApprover();
    try {
      const r = await api.get('/companies/' + companyId + '/claims?per_page=200' + (mine ? '&mine=1' : ''));
      if(seq !== loadSeq) return;
      rows.value = r.data || []; err.value = '';
    } catch(e){
      if(seq !== loadSeq) return;
      rows.value = []; err.value = (e.data && e.data.error) || t('approvals.err_load', 'Could not load approvals');
    }
    if(seq === loadSeq) loading.value = false;
    refreshHeaderCounts(companyId);
  }
  // reload on mount and when the approver verdict resolves (mine=1 vs full queue)
  let lastApprover;
  effect(() => { const a = isApprover(); if(a !== lastApprover){ lastApprover = a; load(); } });

  const setRowErr = (id, msg) => { rowErr.value = { ...rowErr.value, [id]: msg }; };

  async function decide(r, path, body, okMsg){
    setRowErr(r.id, '');
    try {
      const res = await api.post('/companies/' + companyId + '/claims/' + r.id + '/' + path, body || {});
      if(res && res.error){ setRowErr(r.id, res.error); return; }
      await load();
    } catch(e){ setRowErr(r.id, (e.data && e.data.error) || e.message || t('approvals.action_failed', 'Could not complete that')); }
  }
  const approve = r => decide(r, 'status', { status: 'approved' });

  function openReject(r){
    const node = html`<div class="modal-overlay"><div class="modal-panel">
      <div class="modal-title">${t('approvals.reject_title', 'Reject expense')} — ${r.spent_at || r.description || ''}</div>
      <p class="muted">${t('approvals.reject_says', 'Add a reason so the person who submitted it knows why. A reason is required.')}</p>
      <label class="fld"><span>${t('approvals.reject_reason', 'Reason for rejection')} *</span>
        <textarea class="form-control" data-r rows="3" placeholder=${t('approvals.reason_ph', 'e.g. No receipt attached — please resubmit')}></textarea></label>
      <div class="form-err" data-e hidden></div>
      <div class="modal-actions">
        <button type="button" class="btn" data-c>${t('common.cancel', 'Cancel')}</button>
        <button type="button" class="btn bad" data-g>${t('approvals.reject_send', 'Reject & send reason')}</button>
      </div></div></div>`;
    const ov = node.querySelector('.modal-overlay'), ta = ov.querySelector('[data-r]');
    const eE = ov.querySelector('[data-e]'), gE = ov.querySelector('[data-g]');
    const close = () => ov.remove();
    ov.querySelector('[data-c]').addEventListener('click', close);
    gE.addEventListener('click', async () => {
      eE.hidden = true; const reason = ta.value.trim();
      if(!reason){ eE.hidden = false; eE.textContent = t('approvals.reason_required', 'A reason is required to reject.'); ta.focus(); return; }
      gE.disabled = true;
      try {
        const res = await api.post('/companies/' + companyId + '/claims/' + r.id + '/reject', { reason });
        if(res && res.error){ eE.hidden = false; eE.textContent = res.error; gE.disabled = false; return; }
        close(); await load();
      } catch(e){ eE.hidden = false; eE.textContent = (e.data && e.data.error) || e.message; gE.disabled = false; }
    });
    document.body.appendChild(node);
    setTimeout(() => ta.focus(), 30);
  }

  const openExpense = () => navigate('/co/' + companyId + '/expenses');
  const matches = (r, needle) => [r.spent_at, r.description, r.employee_name, r.reject_reason, r.account_name]
    .some(v => v && String(v).toLowerCase().includes(needle));

  const pendingGrid = () => {
    if(loading.value) return html`<div class="muted-block">${t('common.loading', 'Loading…')}</div>`;
    if(err.value) return html`<div class="muted-block">${err.value}</div>`;
    const needle = pQ.value.trim().toLowerCase(), s = sym(), df = dfmt(), errs = rowErr.value;
    const approver = isApprover();
    const body = rows.value.filter(r => PENDING.includes(r.status) && (!needle || matches(r, needle)));
    if(!body.length) return html`<div class="empty">${approver ? t('approvals.none', "Nothing awaiting approval — you're clear.") : t('approvals.none_mine', 'None of your expenses are awaiting approval.')}</div>`;
    return html`<div class="tbl-wrap"><table class="list apv-tbl">
      <thead><tr>
        <th>${t('approvals.col_date', 'Spent on')}</th>
        <th>${t('approvals.col_what', 'What / merchant')}</th>
        <th>${t('approvals.col_who', 'Person')}</th>
        <th class="ta-r">${t('doc.col_total', 'Amount')}</th>
        <th>${t('approvals.col_status', 'Status')}</th>
        <th class="ta-r">${t('approvals.col_action', 'Action')}</th>
      </tr></thead>
      <tbody>${body.map(r => {
        const rerr = errs[r.id] || '';
        return html`<tr class="clickable" @click=${openExpense}>
          <td class="mono">${formatDate(r.claim_date, df)}</td>
          <td><b>${r.spent_at || r.description}</b></td>
          <td>${r.employee_name || '—'}</td>
          <td class="ta-r num">${fmtMoney(r.amount, s, false)}</td>
          <td><span class=${stClass(r.status)}>${stLabel(r.status)}</span></td>
          <td class="ta-r nowrap" @click=${e => e.stopPropagation()}>
            ${approver
              ? html`<button type="button" class="btn sm primary" @click=${() => approve(r)}>${t('approvals.approve', 'Approve')}</button>
                  <button type="button" class="btn sm bad" @click=${() => openReject(r)}>${t('approvals.reject', 'Reject')}</button>`
              : html`<span class="muted">${t('approvals.awaiting', 'Awaiting an approver')}</span>`}
            ${rerr ? html`<div class="row-err apv-row-err">${rerr}</div>` : ''}
          </td>
        </tr>`;
      })}</tbody>
    </table></div>`;
  };

  const hasResolved = () => rows.value.some(r => RESOLVED.includes(r.status));
  const resolvedGrid = () => {
    if(loading.value || err.value) return '';
    const needle = rQ.value.trim().toLowerCase(), s = sym(), df = dfmt();
    const body = rows.value.filter(r => RESOLVED.includes(r.status) && (!needle || matches(r, needle)));
    if(!body.length) return html`<div class="empty">${t('approvals.none_filtered', 'Nothing here for this filter.')}</div>`;
    return html`<div class="tbl-wrap"><table class="list apv-tbl">
      <thead><tr>
        <th>${t('approvals.col_date', 'Spent on')}</th>
        <th>${t('approvals.col_what', 'What / merchant')}</th>
        <th>${t('approvals.col_who', 'Person')}</th>
        <th class="ta-r">${t('doc.col_total', 'Amount')}</th>
        <th>${t('approvals.col_outcome', 'Outcome')}</th>
        <th>${t('approvals.col_reason', 'Reason')}</th>
      </tr></thead>
      <tbody>${body.map(r => html`<tr class="clickable" @click=${openExpense}>
        <td class="mono">${formatDate(r.claim_date, df)}</td>
        <td><b>${r.spent_at || r.description}</b></td>
        <td>${r.employee_name || '—'}</td>
        <td class="ta-r num">${fmtMoney(r.amount, s, false)}</td>
        <td><span class=${stClass(r.status)}>${stLabel(r.display_status || r.status)}</span></td>
        <td>${r.status === 'rejected' && r.reject_reason ? html`<span class="apv-reason" title=${r.reject_reason}>“${r.reject_reason}”</span>` : html`<span class="muted">—</span>`}</td>
      </tr>`)}</tbody>
    </table></div>`;
  };

  const content = html`<div class="page page-wide apv-page">
    ${notificationsTabs(companyId, 'approvals')}
    <div class="page-head">
      <div><span class="eyebrow">${() => isApprover() ? t('approvals.eyebrow', 'Approvals') : t('approvals.eyebrow_mine', 'My expenses')}</span>
        <h1>${t('approvals.title', 'Expense approvals')}</h1>
        <p class="muted">${() => isApprover()
          ? t('approvals.subtitle', 'Expense claims submitted by your team — approve, reject or pay them here. (Documents are confirmed on the dashboard and the Sales / Purchases screens.)')
          : t('approvals.subtitle_mine', 'Expenses you have submitted — track their status and read any reasons here.')}</p></div>
    </div>
    ${() => gateOn() ? '' : html`<div class="hint">${t('approvals.gate_off', 'Expense approval is optional and currently OFF for this company — an approver can pay a submitted expense directly. Turn it on in Settings → Expense approvals.')}</div>`}
    <div class="dash-sub apv-sec-lbl">${t('approvals.pending_lbl', 'Awaiting approval')}</div>
    <div class="list-toolbar">
      <input class="search ux-search" type="search" .value=${pQ} @input=${e => { pQ.value = e.target.value; }}
        placeholder=${t('approvals.search_ph', 'e.g. Engen or Sam')} aria-label=${t('approvals.search_pending', 'Search pending expenses')}>
    </div>
    ${pendingGrid}
    <div class=${() => 'apv-resolved-head' + (hasResolved() ? '' : ' jrn-hide')}>
      <div class="dash-sub apv-sec-lbl">${t('approvals.resolved', 'Resolved')}</div>
      <div class="list-toolbar">
        <input class="search ux-search" type="search" .value=${rQ} @input=${e => { rQ.value = e.target.value; }}
          placeholder=${t('approvals.search_ph2', 'e.g. rejected fuel')} aria-label=${t('approvals.search_resolved', 'Search resolved expenses')}>
      </div>
    </div>
    ${resolvedGrid}
  </div>`;

  return tenantShell(companyId, t('approvals.title', 'Expense approvals'), content);
}
